///
/// EarthBrowser
///
/// Created by Matt Giger on 1/19/2024
/// Copyright (c) 2024 Geodata Labs
///

import { TextGeometry } from "@/earthbrowser/entities/TextNode/TextGeometry"
import { TextRenderer } from "@/earthbrowser/entities/TextNode/TextRenderer"
import { type Font } from "@/earthbrowser/libs/opentype"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { white, type color } from "@stovokor/math/color"
import { type Material } from "@stovokor/shader/Material"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class TextNode extends NodeBase implements Material {
	private context: StovokorContext

	public color: color = white
	public transparency: boolean = false
	public available: boolean = true

	private _text: string
	private _font: Font
	private fontSize: number

	constructor(context: StovokorContext, text: string, font: Font, fontSize: number) {
		super()
		this.context = context
		this._text = text
		this._font = font
		this.fontSize = fontSize

		// avoid creating a text geometry if the font is not loaded
		// Check if the font has been fully loaded (has glyphs data)
		if(this.isFontLoaded(font)) {
			const textGeom = new TextGeometry(context, text, font, fontSize)
			this.renderer = new TextRenderer(context, textGeom, this)
		}
	}

	private isFontLoaded(font: Font): boolean {
		// Check if font has glyphs object and either:
		// - has nGlyphs property set (indicates loaded font), or
		// - _push function exists (for low memory mode)
		return !!(font.glyphs && ((font as unknown as {nGlyphs: number}).nGlyphs || (font as unknown as {_push: unknown})._push))
	}

	public get text(): string {
		return this._text
	}

	public set text(text: string) {
		if(text !== this._text) {
			this._text = text
			// Only update geometry if font is loaded
			if(this.renderer && this.isFontLoaded(this._font)) {
				this.renderer.geometry.destroy()
				this.renderer.geometry = new TextGeometry(this.context, text, this._font, this.fontSize)
			}
		}
	}
}
